import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Highlighter from '../components/Highlighter';
import LocalBusinessSchema from '../components/LocalBusinessSchema';
import '../styles/Clockwork.css';

const Product = () => {
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && !isMobile) {
      video.muted = true;
      video.play().catch(err => console.log('Autoplay prevented:', err));
    }
  }, [isMobile]);

  return (
    <div className="clockwork-app">
      <LocalBusinessSchema />
      <Helmet>
        <title>The Product | Xerowrite Handwritten-Style Direct Mail Postcards</title>
        <meta name="description" content="The only direct mail postcard with variable fluorescent pink highlighter and handwritten-style personalization. Proven at scale. Exclusive to Xerowrite." />
        <link rel="canonical" href="https://xerowrite.com/product" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://xerowrite.com/product" />
        <meta property="og:title" content="The Product | Xerowrite" />
        <meta property="og:description" content="The only direct mail postcard with variable fluorescent pink highlighter. Proven at scale." />
      </Helmet>
      <Header />
      
      <main>
        {/* PAGE HEADER */}
        <section className="section">
          <div className="container-narrow">
            <h1 className="page-title">A Postcard Your Customers Will Actually Read</h1>
            <p className="page-subtitle">
              It looks like someone sat down and wrote it specifically for them. Because to them, it does.
            </p>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="section section-gray">
          <div className="container-narrow">
            <p className="section-subtitle">
              Your customer&apos;s first name. A personal message. And a <Highlighter>fluorescent pink highlighter</Highlighter> message with an arrow calling out and circling exactly what you need them to notice, a phone number, an expiration date, a dollar amount. It stops them before they can decide not to read it.
            </p>
            <p className="section-subtitle" style={{marginTop: '1.5rem'}}>
              This level of personalization, specifically the variable data pink highlighter, is something no other supplier in the direct mail industry can offer. We&apos;ve protected it because it&apos;s the core of why our product works. The highlighter draws the eye. The handwritten-style message creates the connection. The combination drives response.
            </p>
            <p className="section-subtitle" style={{marginTop: '1.5rem'}}>
              We mail many millions of these postcards year after year, to customers all over the United States. The response rates have been consistent, and consistently higher than any other format our clients have tested against it.
            </p>
          </div>
        </section>

        {/* VIDEO SHOWCASE */}
        <section className="section" style={{background: '#1a1a1a', padding: '4rem 0'}}>
          <div className="container">
            <div style={{maxWidth: '900px', margin: '0 auto'}}>
              <video
                ref={videoRef}
                style={{width: '100%', height: 'auto', borderRadius: '8px'}}
                autoPlay
                loop
                muted
                playsInline
                aria-label="Xerowrite handwritten-style direct mail postcard with fluorescent pink highlighter"
                title="Xerowrite Product Demo"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_print-showcase-18/artifacts/ghticdr4_Hero%20Loop.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        {/* WHAT'S ON THE POSTCARD */}
        <section className="section section-gray">
          <div className="container-narrow">
            <h2 className="section-title">What&apos;s On The Postcard</h2>
            <div style={{marginTop: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '1rem'}}>
                <CheckCircle size={24} style={{marginRight: '1rem', marginTop: '0.25rem', color: '#FF2D78', flexShrink: 0}} />
                <p style={{margin: 0, fontSize: '1.125rem', lineHeight: '1.8'}}>
                  Variable handwritten-style personalization, name, message, specific details, unique to each recipient
                </p>
              </div>
              <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '1rem'}}>
                <CheckCircle size={24} style={{marginRight: '1rem', marginTop: '0.25rem', color: '#FF2D78', flexShrink: 0}} />
                <p style={{margin: 0, fontSize: '1.125rem', lineHeight: '1.8'}}>
                  Fluorescent pink hand-drawn arrow and circle around the phone number or call-to-action
                </p>
              </div>
              <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '1rem'}}>
                <CheckCircle size={24} style={{marginRight: '1rem', marginTop: '0.25rem', color: '#FF2D78', flexShrink: 0}} />
                <p style={{margin: 0, fontSize: '1.125rem', lineHeight: '1.8'}}>
                  Full-color printed front side, your design, your brand
                </p>
              </div>
              <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '1rem'}}>
                <CheckCircle size={24} style={{marginRight: '1rem', marginTop: '0.25rem', color: '#FF2D78', flexShrink: 0}} />
                <p style={{margin: 0, fontSize: '1.125rem', lineHeight: '1.8'}}>
                  USPS first-class delivery to any address in the United States
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW TO ORDER */}
        <section className="section">
          <div className="container-narrow">
            <h2 className="section-title">From First Call to the Post Office, We Handle Everything</h2>
            
            <div style={{marginTop: '3rem'}}>
              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 1</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>We Learn Your Business</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  We start with a consultation to understand your company, your ideal customer, and your goals. Who are you trying to reach? How often do you want to mail? What does a great response look like for you?
                </p>
              </div>

              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 2</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>We Build Your List</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  Based on your goals, we identify the right mailing list strategy. That might be your existing customer database, a targeted prospect list filtered by income, home value, age, or buying preferences, or saturation mailing along specific postal carrier routes. We find the audience most likely to respond.
                </p>
              </div>

              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 3</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>We Design Your Postcard</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  Once we know your list, we work on both sides of the postcard: the full-color ad side and the handwritten side. We help you choose the right hook, message, offer, and call to action to maximize response rates.
                </p>
              </div>

              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 4</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>You Receive an Invoice</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  When you&apos;re ready to move forward, we send an invoice. Production begins after payment is received.
                </p>
              </div>

              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 5</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>You Approve the Final Proof</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  We send a digital proof for your review and approval before anything is produced.
                </p>
              </div>

              <div style={{marginBottom: '2.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#FF2D78', marginBottom: '0.5rem'}}>Step 6</h3>
                <h4 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem'}}>We Produce and Mail</h4>
                <p style={{fontSize: '1.125rem', lineHeight: '1.8', color: '#666'}}>
                  Your order goes into production immediately after approval and is delivered to the post office the next business day.
                </p>
              </div>
            </div>

            <div className="cta-group" style={{justifyContent: 'center', marginTop: '3rem'}}>
              <Link to="/about" className="btn btn-primary">
                Order Samples
              </Link>
              <a href="tel:8015097884" className="btn btn-secondary">
                <Phone size={20} />
                Call 801.509.7884
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Product;
